import { createContext, useContext, useState, ReactNode } from 'react';

export type AppMode = 'traditional' | 'ai';

interface AppModeContextProps {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
}

const AppModeContext = createContext<AppModeContextProps | undefined>(undefined);

export function AppModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<AppMode>('traditional');

  // We'll handle localStorage in a safer way that handles when localStorage isn't available
  const handleSetMode = (newMode: AppMode) => {
    setMode(newMode);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('app-mode', newMode);
      }
    } catch (error) {
      console.warn('Unable to save app mode preference to localStorage', error);
    }
  };

  // Try to load the saved mode preference
  useState(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedMode = window.localStorage.getItem('app-mode');
        if (savedMode === 'traditional' || savedMode === 'ai') {
          setMode(savedMode);
        }
      }
    } catch (error) {
      console.warn('Unable to load app mode preference from localStorage', error);
    }
  });

  return (
    <AppModeContext.Provider value={{ mode, setMode: handleSetMode }}>
      {children}
    </AppModeContext.Provider>
  );
}

export function useAppMode(): AppModeContextProps {
  const context = useContext(AppModeContext);
  if (!context) {
    throw new Error('useAppMode must be used within an AppModeProvider');
  }
  return context;
} 